<template>
  <div class="egp-root">

    <!-- ── Top banner ──────────────────────────────────────────────────── -->
    <header class="egp-banner">
      <div class="egp-banner__inner">
        <div class="egp-banner__left">
          <button class="egp-back" @click="$router.go(-1)">
            <v-icon small>mdi-arrow-left</v-icon>
            <span>Groups</span>
          </button>
          <h1 class="egp-title">{{ form.name || 'Loading…' }}</h1>
          <div class="egp-meta">
            <span class="egp-badge" :class="group.is_active ? 'egp-badge--active' : 'egp-badge--inactive'">
              <span class="egp-badge__dot"></span>
              {{ group.is_active ? 'Active' : 'Inactive' }}
            </span>
            <span class="egp-meta__sep">·</span>
            <span class="egp-badge egp-badge--cycle" :class="`egp-badge--cycle-${(form.collecting_cycle||'').toLowerCase()}`">
              <v-icon x-small class="mr-1">mdi-recycle</v-icon>
              {{ form.collecting_cycle || '—' }}
            </span>
            <span class="egp-meta__sep">·</span>
            <span class="egp-id">ID #{{ groupId }}</span>
          </div>
        </div>

        <div class="egp-banner__stats">
          <div class="egp-stat">
            <span class="egp-stat__val">{{ members.length }}</span>
            <span class="egp-stat__lbl">Members</span>
          </div>
          <div class="egp-stat__div"></div>
          <div class="egp-stat">
            <span class="egp-stat__val">{{ currentCycle }}</span>
            <span class="egp-stat__lbl">Cycle</span>
          </div>
          <div class="egp-stat__div"></div>
          <div class="egp-stat">
            <span class="egp-stat__val">{{ form.no_collected_sessions }}</span>
            <span class="egp-stat__lbl">Sessions</span>
          </div>
          <div class="egp-stat__div"></div>
          <div class="egp-stat">
            <span class="egp-stat__val egp-stat__val--gold">
              {{ (form.contribution_amount || 0).toLocaleString() }}
            </span>
            <span class="egp-stat__lbl">FCFA / turn</span>
          </div>
        </div>
      </div>
    </header>

    <!-- ── Loading ─────────────────────────────────────────────────────── -->
    <div v-if="initialLoading" class="egp-loading">
      <v-progress-circular indeterminate color="#D4A017" size="52" width="3" />
      <p>Loading group data…</p>
    </div>

    <div v-else-if="loadError" class="egp-error-wrap">
      <v-alert type="error" prominent class="mb-0">
        <strong>Error loading group:</strong> {{ loadError }}
        <template v-slot:append>
          <v-btn text small @click="loadGroup">Retry</v-btn>
        </template>
      </v-alert>
    </div>

    <!-- ── Main layout ─────────────────────────────────────────────────── -->
    <div v-else class="egp-body">

      <!-- Toasts -->
      <v-snackbar v-model="snackSuccess" color="#2E7D52" top right timeout="3500" rounded="pill">
        <v-icon left small>mdi-check-circle</v-icon> {{ snackSuccessMsg }}
      </v-snackbar>
      <v-snackbar v-model="snackError" color="#B91C1C" top right timeout="5000" rounded="pill">
        <v-icon left small>mdi-alert-circle</v-icon> {{ snackErrorMsg }}
      </v-snackbar>

      <!-- ── Sidebar nav ──────────────────────────────────────────────── -->
      <nav class="egp-sidenav">
        <button
          v-for="(tab, i) in tabs"
          :key="tab.key"
          class="egp-sidenav__item"
          :class="{ 'is-active': activeTab === i }"
          @click="activeTab = i"
          type="button"
        >
          <v-icon small class="egp-sidenav__icon">{{ tab.icon }}</v-icon>
          <span class="egp-sidenav__label">{{ tab.label }}</span>
          <span v-if="tab.key === 'actions'" class="egp-sidenav__alert">!</span>
        </button>

        <!-- Compact status strip at sidebar bottom -->
        <div class="egp-sidenav__foot">
          <div class="egp-sidenav__status" :class="form.collection_activated ? 'is-on' : 'is-off'">
            <span class="egp-sidenav__status-dot"></span>
            {{ form.collection_activated ? 'Collection on' : 'Collection off' }}
          </div>
        </div>
      </nav>

      <!-- ── Content pane ────────────────────────────────────────────── -->
      <main class="egp-content">

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- TAB 0 — Basic Info                                           -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        <section v-show="activeTab === 0" class="egp-section">
          <div class="egp-section__header">
            <h2>Basic Information</h2>
            <p>The group's name, contact, and background details.</p>
          </div>

          <div class="egp-form-grid egp-form-grid--3">
            <v-text-field
              v-model="form.name"
              label="Group Name"
              prepend-inner-icon="mdi-account-group"
              outlined dense
              :rules="[v => !!v || 'Name is required']"
              counter="255" maxlength="255"
              class="egp-field"
            />
            <v-text-field
              v-model="form.njangi_contact"
              label="Contact Number"
              prepend-inner-icon="mdi-phone"
              outlined dense
              class="egp-field"
            />
            <v-text-field
              v-model.number="form.contribution_amount"
              label="Contribution Amount (FCFA)"
              prepend-inner-icon="mdi-cash"
              outlined dense type="number" min="500"
              :rules="[v => v >= 500 || 'Minimum 500 FCFA']"
              class="egp-field"
            />
          </div>

          <v-textarea
            v-model="form.information"
            label="Group Description"
            prepend-inner-icon="mdi-text"
            outlined dense rows="3"
            counter="2000"
            class="egp-field"
          />

          <div class="egp-form-grid egp-form-grid--2">
            <v-textarea
              v-model="form.rules"
              label="Group Rules"
              prepend-inner-icon="mdi-gavel"
              outlined dense rows="4"
              class="egp-field"
            />
            <v-textarea
              v-model="form.history"
              label="Group History"
              prepend-inner-icon="mdi-history"
              outlined dense rows="4"
              class="egp-field"
            />
          </div>

          <div class="egp-section__footer">
            <v-btn class="egp-btn egp-btn--primary" :loading="saving.info" @click="saveBasicInfo" depressed>
              <v-icon left small>mdi-content-save</v-icon> Save Basic Info
            </v-btn>
          </div>
        </section>

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- TAB 1 — Dates                                                -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        <section v-show="activeTab === 1" class="egp-section">
          <div class="egp-section__header">
            <h2>Dates &amp; Cycle State</h2>
            <p>Adjust start date, next collection date, and the current cycle position.</p>
          </div>

          <div class="egp-caution-banner">
            <v-icon color="#92400E" small class="mr-2">mdi-alert</v-icon>
            <span>
              <strong>Proceed with care.</strong>
              Changes here directly affect contribution history and penalty calculations.
            </span>
          </div>

          <div class="egp-form-grid egp-form-grid--2 mt-5">
            <v-menu v-model="dateMenuStart" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.start_date"
                  label="Start Date"
                  prepend-inner-icon="mdi-calendar-start"
                  outlined dense readonly clearable
                  v-bind="attrs" v-on="on"
                  hint="Date the group started collecting"
                  persistent-hint
                  class="egp-field"
                />
              </template>
              <v-date-picker v-model="form.start_date" @input="dateMenuStart = false" color="#1C3A5E" />
            </v-menu>

            <v-menu v-model="dateMenuCollection" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.collection_date"
                  label="Next Collection Date"
                  prepend-inner-icon="mdi-calendar-check"
                  outlined dense readonly clearable
                  v-bind="attrs" v-on="on"
                  :hint="collectionDateHint"
                  persistent-hint
                  class="egp-field"
                />
              </template>
              <v-date-picker v-model="form.collection_date" @input="dateMenuCollection = false" color="#1C3A5E" />
            </v-menu>

            <v-text-field
              v-model.number="form.no_collected_sessions"
              label="Sessions Completed"
              prepend-inner-icon="mdi-counter"
              outlined dense type="number" min="0"
              hint="Total collection sessions done so far"
              persistent-hint
              class="egp-field"
            />
            <v-text-field
              v-model.number="form.current_collect_position"
              label="Current Collector Position"
              prepend-inner-icon="mdi-map-marker-account"
              outlined dense type="number" min="1"
              hint="Position number of the member currently collecting"
              persistent-hint
              class="egp-field"
            />
          </div>

          <div class="egp-computed-strip">
            <div class="egp-computed-item">
              <span class="egp-computed-item__lbl">Current cycle</span>
              <span class="egp-computed-item__val">{{ currentCycle }}</span>
            </div>
            <div class="egp-computed-item">
              <span class="egp-computed-item__lbl">Current collector</span>
              <span class="egp-computed-item__val">{{ group.current_collector || '—' }}</span>
            </div>
            <div class="egp-computed-item">
              <span class="egp-computed-item__lbl">Total members</span>
              <span class="egp-computed-item__val">{{ members.length }}</span>
            </div>
            <div class="egp-computed-item">
              <span class="egp-computed-item__lbl">Positions left</span>
              <span class="egp-computed-item__val">{{ Math.max(0, members.length - (form.current_collect_position - 1)) }}</span>
            </div>
          </div>

          <div class="egp-section__footer">
            <v-btn class="egp-btn egp-btn--primary" :loading="saving.dates" @click="saveDates" depressed>
              <v-icon left small>mdi-content-save</v-icon> Save Dates &amp; Cycle State
            </v-btn>
          </div>
        </section>

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- TAB 2 — Collection Order                                     -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        <section v-show="activeTab === 2" class="egp-section">
          <div class="egp-section__header">
            <h2>Collection Order</h2>
            <p>Set each member's position in the rotation. Position 1 collects first.</p>
          </div>

          <div v-if="loadingMembers" class="text-center py-8">
            <v-progress-circular indeterminate color="#D4A017" />
          </div>

          <template v-else>

            <!-- Current collector callout -->
            <div v-if="currentCollectorMember" class="egp-collector-callout">
              <div class="egp-collector-callout__avatar" :style="{ background: getAvatarColor(currentCollectorMember.member_name) }">
                {{ currentCollectorMember.member_name.charAt(0).toUpperCase() }}
              </div>
              <div class="egp-collector-callout__info">
                <div class="egp-collector-callout__eyebrow">Collecting this cycle</div>
                <div class="egp-collector-callout__name">{{ currentCollectorMember.member_name }}</div>
                <div class="egp-collector-callout__pos">Position {{ form.current_collect_position }} of {{ members.length }}</div>
              </div>
              <div class="egp-collector-callout__pool">
                <span class="egp-collector-callout__pool-amount">
                  {{ ((form.contribution_amount || 0) * Math.max(0, members.length - 1)).toLocaleString() }}
                </span>
                <span class="egp-collector-callout__pool-lbl">FCFA expected pool</span>
              </div>
            </div>

            <!-- Members list -->
            <div class="egp-members-list">
              <div class="egp-members-list__head">
                <span>Pos.</span>
                <span>Member</span>
                <span>Status</span>
                <span>Cycle month</span>
                <span></span>
              </div>
              <div
                v-for="item in sortedPositions"
                :key="item.member_id"
                class="egp-member-row"
                :class="{ 'is-current': item.position === form.current_collect_position }"
              >
                <div class="egp-member-row__pos">
                  <v-text-field
                    v-model.number="item.position"
                    type="number" min="1" :max="editablePositions.length"
                    dense outlined hide-details
                    class="egp-pos-input"
                    @change="markPositionDirty(item)"
                  />
                </div>
                <div class="egp-member-row__info">
                  <div class="egp-member-avatar" :style="{ background: getAvatarColor(item.member_name) }">
                    {{ item.member_name.charAt(0).toUpperCase() }}
                  </div>
                  <span class="egp-member-row__name">{{ item.member_name }}</span>
                </div>
                <div class="egp-member-row__status">
                  <span class="egp-status-dot" :class="item.status === 'Ongoing' ? 'is-active' : 'is-done'"></span>
                  {{ item.status }}
                </div>
                <div class="egp-member-row__month">
                  <span v-if="item.cycle_month" class="egp-month-chip">{{ item.cycle_month }}</span>
                  <span v-else class="egp-month-chip egp-month-chip--empty">TBD</span>
                </div>
                <div class="egp-member-row__star">
                  <v-icon v-if="item.position === form.current_collect_position" color="#D4A017" small>mdi-star</v-icon>
                </div>
              </div>
            </div>

            <v-alert v-if="hasPositionConflicts" type="error" dense border="left" class="mt-4">
              <strong>Duplicate positions detected.</strong> All positions must be unique before saving.
            </v-alert>

            <div class="egp-section__footer egp-section__footer--split">
              <v-btn text small color="#64748B" @click="autoNumberPositions">
                <v-icon left small>mdi-auto-fix</v-icon> Auto-number
              </v-btn>
              <v-btn
                class="egp-btn egp-btn--primary"
                :loading="saving.positions"
                :disabled="hasPositionConflicts"
                @click="savePositions"
                depressed
              >
                <v-icon left small>mdi-content-save</v-icon> Save Order
              </v-btn>
            </div>
          </template>
        </section>

        <!-- ══════════════════════════════════════════════════════════════ -->
        <!-- TAB 3 — Quick Actions                                        -->
        <!-- ══════════════════════════════════════════════════════════════ -->
        <section v-show="activeTab === 3" class="egp-section">
          <div class="egp-section__header">
            <h2>Quick Actions</h2>
            <p>Manage collection state, reset cycles, send reminders, or delete the group.</p>
          </div>

          <div class="egp-actions-grid">

            <!-- Toggle activation -->
            <div class="egp-action-card" :class="form.collection_activated ? 'egp-action-card--on' : 'egp-action-card--idle'">
              <div class="egp-action-card__icon-wrap">
                <v-icon size="28" :color="form.collection_activated ? '#2E7D52' : '#94A3B8'">
                  {{ form.collection_activated ? 'mdi-check-circle-outline' : 'mdi-pause-circle-outline' }}
                </v-icon>
              </div>
              <h3 class="egp-action-card__title">Collection State</h3>
              <p class="egp-action-card__desc">
                {{ form.collection_activated
                  ? 'Collection is active. Members can pay contributions and run cycles.'
                  : 'Collection is paused. Contributions are locked.' }}
              </p>
              <v-btn
                :color="form.collection_activated ? '#B91C1C' : '#2E7D52'"
                outlined depressed rounded small
                :loading="saving.activate"
                @click="toggleActivation"
                class="egp-action-card__btn"
              >
                <v-icon left small>
                  {{ form.collection_activated ? 'mdi-pause' : 'mdi-play' }}
                </v-icon>
                {{ form.collection_activated ? 'Pause collection' : 'Activate collection' }}
              </v-btn>
            </div>

            <!-- Reset rotation -->
            <div class="egp-action-card">
              <div class="egp-action-card__icon-wrap">
                <v-icon size="28" color="#B45309">mdi-rotate-left</v-icon>
              </div>
              <h3 class="egp-action-card__title">Reset Cycle</h3>
              <p class="egp-action-card__desc">
                Notify members who haven't contributed yet this period.
                Only non-contributors will receive a message.
              </p>
              <v-btn
                color="#1C3A5E" dark depressed rounded small
                :loading="saving.reminders"
                @click="sendReminders"
                class="egp-action-card__btn"
              >
                <v-icon left small>mdi-send</v-icon> Send reminders
              </v-btn>
            </div>

            <!-- Danger zone -->
            <div class="egp-action-card egp-action-card--danger">
              <div class="egp-action-card__icon-wrap">
                <v-icon size="28" color="#B91C1C">mdi-delete-forever</v-icon>
              </div>
              <h3 class="egp-action-card__title egp-action-card__title--danger">Delete Group</h3>
              <p class="egp-action-card__desc">
                Permanently removes this group, all members, transactions, and history.
                <strong>This cannot be undone.</strong>
              </p>
              <v-btn
                color="#B91C1C" outlined depressed rounded small
                :loading="saving.delete"
                @click="confirmDelete"
                class="egp-action-card__btn"
              >
                <v-icon left small>mdi-delete-forever</v-icon> Delete group permanently
              </v-btn>
            </div>

          </div>
        </section>

      </main>
    </div>

    <!-- ── Confirm Reset Dialog ────────────────────────────────────────── -->
    <v-dialog v-model="resetConfirmDialog" max-width="440">
      <v-card class="egp-dialog">
        <div class="egp-dialog__header egp-dialog__header--warn">
          <v-icon color="white" class="mr-2">mdi-rotate-left</v-icon>
          Confirm Cycle Reset
        </div>
        <div class="egp-dialog__body">
          <v-alert type="warning" dense border="left" class="mb-3">
            This resets the cycle to <strong>Position 1</strong> and increments the cycle count. The action will be logged.
          </v-alert>
          <p class="body-2 mt-2"><strong>Reason:</strong> {{ resetReason }}</p>
        </div>
        <div class="egp-dialog__actions">
          <v-btn text @click="resetConfirmDialog = false">Cancel</v-btn>
          <v-btn color="#B45309" dark depressed rounded :loading="saving.reset" @click="doResetCycle">
            <v-icon left small>mdi-rotate-left</v-icon> Confirm Reset
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Confirm Delete Dialog ───────────────────────────────────────── -->
    <v-dialog v-model="deleteConfirmDialog" max-width="440">
      <v-card class="egp-dialog">
        <div class="egp-dialog__header egp-dialog__header--danger">
          <v-icon color="white" class="mr-2">mdi-delete-forever</v-icon>
          Delete Group
        </div>
        <div class="egp-dialog__body">
          <v-alert type="error" dense border="left" class="mb-3">
            <strong>Irreversible.</strong> All data for <strong>{{ form.name }}</strong> will be permanently deleted.
          </v-alert>
          <v-text-field
            v-model="deleteConfirmText"
            :label="`Type '${form.name}' to confirm`"
            outlined dense
            class="egp-field mt-3"
          />
        </div>
        <div class="egp-dialog__actions">
          <v-btn text @click="deleteConfirmDialog = false">Cancel</v-btn>
          <v-btn
            color="#B91C1C" dark depressed rounded
            :disabled="deleteConfirmText !== form.name"
            :loading="saving.delete"
            @click="doDelete"
          >
            <v-icon left small>mdi-delete-forever</v-icon> Delete permanently
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

  </div>
</template>


<script>
import axios from 'axios'

const AVATAR_COLORS = [
  '#1C3A5E','#2E7D52','#B45309','#7C3AED','#B91C1C',
  '#0E7490','#4D7C0F','#C2410C','#5B21B6','#1E40AF',
]

export default {
  name: 'EditNjangiGroup',

  data() {
    return {
      groupId: null,
      initialLoading: true,
      loadError: null,
      loadingMembers: false,
      activeTab: 0,

      tabs: [
        { key: 'info',     label: 'Basic Info',       icon: 'mdi-information-outline' },
        { key: 'dates',    label: 'Dates',             icon: 'mdi-calendar-range' },
        { key: 'order',    label: 'Collection Order',  icon: 'mdi-sort-numeric-ascending' },
        { key: 'actions',  label: 'Actions',           icon: 'mdi-lightning-bolt' },
      ],

      cycleOptions: ['Daily', 'Weekly', 'Monthly', 'Custom'],

      group: {},

      form: {
        name: '',
        njangi_contact: '',
        information: '',
        rules: '',
        history: '',
        is_active: true,
        collecting_cycle: '',
        contribution_amount: 0,
        collection_activated: false,
        custom_interval_value: null,
        custom_interval_unit: null,
        custom_penalty_amount: null,
        no_people_collect_per_cycle: 1,
        start_date: null,
        collection_date: null,
        no_collected_sessions: 0,
        current_collect_position: 1,
      },

      members: [],
      editablePositions: [],
      dirtyPositions: new Set(),

      dateMenuStart: false,
      dateMenuCollection: false,

      resetReason: '',
      resetConfirmDialog: false,

      deleteConfirmDialog: false,
      deleteConfirmText: '',

      saving: {
        info: false, settings: false, dates: false,
        positions: false, activate: false, reset: false,
        reminders: false, delete: false,
      },

      snackSuccess: false, snackSuccessMsg: '',
      snackError:   false, snackErrorMsg:   '',
    }
  },

  computed: {
    currentCycle() {
      const sessions = this.form.no_collected_sessions || 0
      const total    = this.members.length || 1
      return Math.floor(sessions / total) + 1
    },

    currentCollectorMember() {
      return this.editablePositions.find(
        m => m.position === this.form.current_collect_position
      ) || null
    },

    sortedPositions() {
      return [...this.editablePositions].sort((a, b) => a.position - b.position)
    },

    collectionDateHint() {
      if (this.form.collecting_cycle === 'Monthly') {
        return 'Set to the collection month. Contributions open 20th of prior month.'
      }
      if (this.form.collecting_cycle === 'Weekly') {
        return 'The Monday of the current or next collection week.'
      }
      return 'Next scheduled collection date'
    },

    hasPositionConflicts() {
      const positions = this.editablePositions.map(m => m.position)
      return new Set(positions).size !== positions.length
    },
  },

  async mounted() {
    document.title = 'Edit Group — Njangi Manager'
    this.groupId = parseInt(this.$route.params.id)
    await this.loadGroup()
    await this.loadMembers()
  },

  methods: {
    cycleIcon(cycle) {
      const map = {
        Daily: 'mdi-white-balance-sunny',
        Weekly: 'mdi-calendar-week',
        Monthly: 'mdi-calendar-month',
        Custom: 'mdi-tune',
      }
      return map[cycle] || 'mdi-recycle'
    },

    async loadGroup() {
      this.initialLoading = true
      this.loadError = null
      try {
        const res = await axios.get(`/api/v1/manager/njangi/groups/${this.groupId}/`)
        const d = res.data.data || res.data
        this.group = d
        this.form.name                       = d.name || ''
        this.form.njangi_contact             = d.njangi_contact || ''
        this.form.information                = d.information || ''
        this.form.rules                      = d.rules || ''
        this.form.history                    = d.history || ''
        this.form.is_active                  = d.is_active ?? true
        this.form.collecting_cycle           = d.collecting_cycle || ''
        this.form.contribution_amount        = d.contribution_amount || 0
        this.form.collection_activated       = d.collection_activated ?? false
        this.form.custom_interval_value      = d.custom_interval_value || null
        this.form.custom_interval_unit       = d.custom_interval_unit || null
        this.form.custom_penalty_amount      = d.custom_penalty_amount || null
        this.form.no_people_collect_per_cycle = d.no_people_collect_per_cycle || 1
        this.form.no_collected_sessions      = d.no_collected_sessions || 0
        this.form.current_collect_position   = d.current_collect_position || 1
        this.form.start_date                 = this.parseDate(d.start_date)
        this.form.collection_date            = this.parseDate(d.collection_date)
      } catch (err) {
        this.loadError = err.response?.data?.error || err.message || 'Unknown error'
      } finally {
        this.initialLoading = false
      }
    },

    async loadMembers() {
      this.loadingMembers = true
      try {
        const res = await axios.get(`/api/v1/manager/njangi/group/${this.groupId}/member-positions/`)
        this.members = res.data || []
        this.editablePositions = this.members.map(m => ({
          member_id:   m.member_id,
          member_name: m.member_name,
          status:      m.status || 'Ongoing',
          position:    m.position,
          cycle_month: this.computeCycleMonth(m.position),
        }))
      } catch (err) {
        console.error('Error loading members:', err)
      } finally {
        this.loadingMembers = false
      }
    },

    async saveBasicInfo() {
      this.saving.info = true
      try {
        await axios.put(`/api/v1/manager/njangi/groups/${this.groupId}/update/`, {
          name: this.form.name,
          njangi_contact: this.form.njangi_contact,
          information: this.form.information,
          rules: this.form.rules,
          history: this.form.history,
          is_active: this.form.is_active,
          contribution_amount: this.form.contribution_amount,
        })
        this.showSuccess('Basic information saved.')
        await this.loadGroup()
      } catch (err) {
        this.showError(err.response?.data?.error || 'Error saving basic info')
      } finally {
        this.saving.info = false
      }
    },

    async saveDates() {
      this.saving.dates = true
      try {
        await axios.put(`/api/v1/manager/njangi/groups/${this.groupId}/update/`, {
          start_date: this.form.start_date || null,
          collection_date: this.form.collection_date || null,
          no_collected_sessions: this.form.no_collected_sessions,
          current_collect_position: this.form.current_collect_position,
        })
        this.showSuccess('Dates and cycle state saved.')
        await this.loadGroup()
        this.loadMembers()
      } catch (err) {
        this.showError(err.response?.data?.error || 'Error saving dates')
      } finally {
        this.saving.dates = false
      }
    },

    async savePositions() {
      if (this.hasPositionConflicts) return
      this.saving.positions = true
      const errors = []
      try {
        const requests = this.editablePositions
          .filter(m => this.dirtyPositions.has(m.member_id))
          .map(m => {
            const oldPos = this.members.find(x => x.member_id === m.member_id)?.position
            if (oldPos == null || oldPos === '') {
              errors.push(`${m.member_name}: pas de position initiale (ignorer)`)
              return Promise.resolve()
            }
            return axios.post('/api/v1/manager/njangi/update-member-position/', {
              group_id:     this.groupId,
              member_id:    m.member_id,
              old_position: oldPos,
              new_position: m.position,
            }).catch(err => {
              errors.push(`${m.member_name}: ${err.response?.data?.error || 'erreur'}`)
            })
          })

        await Promise.all(requests)
        this.dirtyPositions.clear()
        if (errors.length) {
          this.showError('Partial errors: ' + errors.join('; '))
        } else {
          this.showSuccess('Collection order saved.')
        }
        await this.loadMembers()
        await this.loadGroup()
      } catch (err) {
        this.showError(err.response?.data?.error || 'Error saving positions')
      } finally {
        this.saving.positions = false
      }
    },

    async toggleActivation() {
      this.saving.activate = true
      try {
        const endpoint = this.form.collection_activated
          ? `/api/v1/manager/njangi/deactivate/${this.groupId}/`
          : `/api/v1/manager/njangi/activate/${this.groupId}/`
        await axios.post(endpoint)
        this.form.collection_activated = !this.form.collection_activated
        this.showSuccess(this.form.collection_activated ? 'Collection activated.' : 'Collection paused.')
      } catch (err) {
        this.showError(err.response?.data?.error || 'Error toggling activation')
      } finally {
        this.saving.activate = false
      }
    },

    confirmResetCycle() {
      if (!this.resetReason.trim()) return
      this.resetConfirmDialog = true
    },

    async doResetCycle() {
      this.saving.reset = true
      try {
        await axios.post(`/api/v1/manager/njangi/reset-cycle/${this.groupId}/`, { reason: this.resetReason })
        this.showSuccess('Cycle reset to position 1.')
        this.resetConfirmDialog = false
        this.resetReason = ''
        await this.loadGroup()
        await this.loadMembers()
      } catch (err) {
        this.showError(err.response?.data?.error || 'Error resetting cycle')
      } finally {
        this.saving.reset = false
      }
    },

    async sendReminders() {
      this.saving.reminders = true
      try {
        await axios.post(`/api/v1/manager/njangi/groups/${this.groupId}/send-reminders/`)
        this.showSuccess('Reminders sent to non-contributing members.')
      } catch (err) {
        this.showError(err.response?.data?.error || 'Error sending reminders')
      } finally {
        this.saving.reminders = false
      }
    },

    confirmDelete() {
      this.deleteConfirmText = ''
      this.deleteConfirmDialog = true
    },

    async doDelete() {
      if (this.deleteConfirmText !== this.form.name) return
      this.saving.delete = true
      try {
        await axios.delete(`/api/v1/manager/njangi/groups/${this.groupId}/delete/`)
        this.showSuccess('Group deleted.')
        setTimeout(() => this.$router.push('/njangi/view/groups'), 1500)
      } catch (err) {
        this.showError(err.response?.data?.error || 'Error deleting group')
      } finally {
        this.saving.delete = false
        this.deleteConfirmDialog = false
      }
    },

    parseDate(dateStr) {
      if (!dateStr) return null
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr
      try { return new Date(dateStr).toISOString().split('T')[0] } catch { return null }
    },

    computeCycleMonth(position) {
      if (!this.form.start_date || this.form.collecting_cycle !== 'Monthly') return null
      try {
        const start = new Date(this.form.start_date)
        const target = new Date(start.getFullYear(), start.getMonth() + (position - 1), 1)
        return target.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
      } catch { return null }
    },

    autoNumberPositions() {
      [...this.editablePositions]
        .sort((a, b) => a.position - b.position || a.member_name.localeCompare(b.member_name))
        .forEach((m, idx) => {
          m.position = idx + 1
          this.dirtyPositions.add(m.member_id)
        })
      this.editablePositions.forEach(m => {
        m.cycle_month = this.computeCycleMonth(m.position)
      })
    },

    markPositionDirty(item) {
      this.dirtyPositions.add(item.member_id)
      item.cycle_month = this.computeCycleMonth(item.position)
    },

    getAvatarColor(name) {
      const idx = (name.charCodeAt(0) || 0) % AVATAR_COLORS.length
      return AVATAR_COLORS[idx]
    },

    showSuccess(msg) { this.snackSuccessMsg = msg; this.snackSuccess = true },
    showError(msg)   { this.snackErrorMsg = msg;   this.snackError   = true },
  },
}
</script>


<style scoped>
/* ── Tokens ──────────────────────────────────────────────────────────── */
:root {
  --navy:    #1C3A5E;
  --gold:    #D4A017;
  --cream:   #F8F6F1;
  --card:    #FFFFFF;
  --border:  #E2DDD4;
  --success: #2E7D52;
  --warn:    #B45309;
  --danger:  #B91C1C;
  --muted:   #64748B;
  --text:    #1A1A2E;
}

/* ── Root shell ───────────────────────────────────────────────────────── */
.egp-root {
  min-height: 100vh;
  background: #F8F6F1;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* ── Banner ───────────────────────────────────────────────────────────── */
.egp-banner {
  background: #1C3A5E;
  color: #fff;
  padding: 0 24px;
  border-bottom: 3px solid #D4A017;
}
.egp-banner__inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 0 16px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}
.egp-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  color: #94A3B8;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 6px;
  transition: color 0.15s;
}
.egp-back:hover { color: #CBD5E1; }
.egp-title {
  font-size: 1.5rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  margin: 0 0 8px;
  color: #fff;
}
.egp-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.egp-meta__sep { color: #475569; font-size: 0.8rem; }
.egp-id { font-size: 0.78rem; color: #94A3B8; }

/* Badges */
.egp-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 20px;
}
.egp-badge__dot {
  width: 6px; height: 6px;
  border-radius: 50%;
  display: inline-block;
}
.egp-badge--active  { background: rgba(46,125,82,0.25); color: #6EE7A0; }
.egp-badge--active .egp-badge__dot { background: #6EE7A0; }
.egp-badge--inactive { background: rgba(148,163,184,0.2); color: #94A3B8; }
.egp-badge--inactive .egp-badge__dot { background: #94A3B8; }
.egp-badge--cycle { background: rgba(212,160,23,0.2); color: #F5C842; }
.egp-badge--cycle-daily   { background: rgba(59,130,246,0.2); color: #93C5FD; }
.egp-badge--cycle-weekly  { background: rgba(46,125,82,0.2);  color: #6EE7A0; }
.egp-badge--cycle-monthly { background: rgba(167,139,250,0.2); color: #C4B5FD; }
.egp-badge--cycle-custom  { background: rgba(212,160,23,0.2); color: #F5C842; }

/* Stats strip */
.egp-banner__stats {
  display: flex;
  align-items: center;
  gap: 0;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px;
  padding: 10px 20px;
}
.egp-stat { text-align: center; padding: 0 16px; }
.egp-stat__div {
  width: 1px; height: 32px;
  background: rgba(255,255,255,0.15);
}
.egp-stat__val {
  display: block;
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}
.egp-stat__val--gold { color: #F5C842; }
.egp-stat__lbl {
  display: block;
  font-size: 0.68rem;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 3px;
}

/* ── Loading / Error ─────────────────────────────────────────────────── */
.egp-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  gap: 16px;
  color: #64748B;
}
.egp-error-wrap {
  max-width: 700px;
  margin: 40px auto;
  padding: 0 24px;
}

/* ── Body layout ─────────────────────────────────────────────────────── */
.egp-body {
  display: flex;
  max-width: 1200px;
  margin: 0 auto;
  padding: 28px 24px;
  gap: 24px;
  align-items: flex-start;
}

/* ── Sidebar nav ─────────────────────────────────────────────────────── */
.egp-sidenav {
  flex: 0 0 210px;
  width: 210px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E2DDD4;
  padding: 8px;
  position: sticky;
  top: 24px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}
.egp-sidenav__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border-radius: 8px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #475569;
  text-align: left;
  transition: background 0.15s, color 0.15s;
  position: relative;
}
.egp-sidenav__item:hover { background: #F8F6F1; color: #1C3A5E; }
.egp-sidenav__item.is-active {
  background: #EEF3F9;
  color: #1C3A5E;
  font-weight: 600;
}
.egp-sidenav__item.is-active .egp-sidenav__icon {
  color: #1C3A5E !important;
}
.egp-sidenav__icon { color: #94A3B8; }
.egp-sidenav__label { flex: 1; }
.egp-sidenav__alert {
  font-size: 0.65rem;
  font-weight: 700;
  background: #B91C1C;
  color: white;
  width: 16px; height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
}
.egp-sidenav__foot {
  margin-top: 8px;
  padding: 10px 12px;
  border-top: 1px solid #F1EDE6;
}
.egp-sidenav__status {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748B;
}
.egp-sidenav__status-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.egp-sidenav__status.is-on .egp-sidenav__status-dot { background: #2E7D52; box-shadow: 0 0 0 2px #DCFCE7; }
.egp-sidenav__status.is-off .egp-sidenav__status-dot { background: #94A3B8; }
.egp-sidenav__status.is-on { color: #2E7D52; }

/* ── Content pane ────────────────────────────────────────────────────── */
.egp-content {
  flex: 1;
  min-width: 0;
}

/* ── Sections ────────────────────────────────────────────────────────── */
.egp-section {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E2DDD4;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  padding: 28px 28px 20px;
}
.egp-section__header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #F1EDE6;
}
.egp-section__header h2 {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1A1A2E;
  margin: 0 0 4px;
}
.egp-section__header p {
  font-size: 0.85rem;
  color: #64748B;
  margin: 0;
}
.egp-section__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #F1EDE6;
}
.egp-section__footer--split {
  justify-content: space-between;
  align-items: center;
}

/* ── Form helpers ────────────────────────────────────────────────────── */
.egp-field { margin-bottom: 4px !important; }
.egp-field-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.egp-field-block { margin-bottom: 4px; }
.egp-form-grid {
  display: grid;
  gap: 16px;
  margin-bottom: 4px;
}
.egp-form-grid--2 { grid-template-columns: repeat(2, 1fr); }
.egp-form-grid--3 { grid-template-columns: repeat(3, 1fr); }

/* Cycle pills — the signature selector element */
.egp-cycle-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.egp-cycle-pill {
  display: inline-flex;
  align-items: center;
  padding: 7px 16px;
  border-radius: 20px;
  border: 1.5px solid #E2DDD4;
  background: #F8F6F1;
  font-size: 0.85rem;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s;
}
.egp-cycle-pill:hover {
  border-color: #1C3A5E;
  color: #1C3A5E;
  background: #EEF3F9;
}
.egp-cycle-pill.is-selected {
  background: #1C3A5E;
  border-color: #1C3A5E;
  color: #fff;
  font-weight: 600;
}

/* Custom box */
.egp-custom-box {
  background: #FFF9ED;
  border: 1px solid #FDE68A;
  border-radius: 10px;
  padding: 16px 20px;
  margin-bottom: 16px;
}
.egp-custom-box__title {
  font-size: 0.82rem;
  font-weight: 600;
  color: #B45309;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
}

/* Toggle row */
.egp-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #F8F6F1;
  border: 1px solid #E2DDD4;
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 4px;
}
.egp-toggle-row__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1A1A2E;
  margin: 0 0 3px;
}
.egp-toggle-row__desc {
  font-size: 0.8rem;
  color: #64748B;
  margin: 0;
  transition: color 0.15s;
}
.egp-toggle-row__desc.is-on  { color: #2E7D52; }
.egp-toggle-row__desc.is-off { color: #94A3B8; }

/* Caution banner */
.egp-caution-banner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #FFF9ED;
  border: 1px solid #FDE68A;
  border-left: 4px solid #B45309;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 0.85rem;
  color: #78350F;
}

/* Computed strip */
.egp-computed-strip {
  display: flex;
  gap: 0;
  background: #F8F6F1;
  border: 1px solid #E2DDD4;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 20px;
}
.egp-computed-item {
  flex: 1;
  padding: 14px 20px;
  border-right: 1px solid #E2DDD4;
  text-align: center;
}
.egp-computed-item:last-child { border-right: none; }
.egp-computed-item__lbl {
  display: block;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #94A3B8;
  margin-bottom: 4px;
}
.egp-computed-item__val {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: #1C3A5E;
}

/* ── Collector callout ───────────────────────────────────────────────── */
.egp-collector-callout {
  display: flex;
  align-items: center;
  gap: 16px;
  background: linear-gradient(135deg, #FFF8E1 0%, #FFFBF0 100%);
  border: 1.5px solid #FDE68A;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 20px;
}
.egp-collector-callout__avatar {
  width: 44px; height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  color: white;
  flex-shrink: 0;
}
.egp-collector-callout__eyebrow {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: #B45309;
  margin-bottom: 2px;
}
.egp-collector-callout__name {
  font-size: 1rem;
  font-weight: 700;
  color: #1A1A2E;
}
.egp-collector-callout__pos {
  font-size: 0.8rem;
  color: #64748B;
}
.egp-collector-callout__pool {
  margin-left: auto;
  text-align: right;
  flex-shrink: 0;
}
.egp-collector-callout__pool-amount {
  display: block;
  font-size: 1.4rem;
  font-weight: 800;
  color: #B45309;
  line-height: 1;
}
.egp-collector-callout__pool-lbl {
  font-size: 0.7rem;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

/* ── Members list ────────────────────────────────────────────────────── */
.egp-members-list {
  border: 1px solid #E2DDD4;
  border-radius: 10px;
  overflow: hidden;
}
.egp-members-list__head {
  display: grid;
  grid-template-columns: 80px 1fr 100px 130px 36px;
  gap: 12px;
  padding: 10px 16px;
  background: #F8F6F1;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94A3B8;
  border-bottom: 1px solid #E2DDD4;
}
.egp-member-row {
  display: grid;
  grid-template-columns: 80px 1fr 100px 130px 36px;
  gap: 12px;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #F1EDE6;
  transition: background 0.1s;
}
.egp-member-row:last-child { border-bottom: none; }
.egp-member-row:hover { background: #FAFAF7; }
.egp-member-row.is-current { background: #FFFBF0; }
.egp-member-row__pos { display: flex; align-items: center; }
.egp-pos-input { width: 68px !important; }
.egp-pos-input >>> .v-input__control { min-height: 34px !important; }
.egp-pos-input >>> .v-input__slot { padding: 0 8px !important; }

.egp-member-row__info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.egp-member-avatar {
  width: 32px; height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}
.egp-member-row__name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1A1A2E;
}
.egp-member-row__status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #64748B;
}
.egp-status-dot {
  width: 7px; height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.egp-status-dot.is-active { background: #2E7D52; }
.egp-status-dot.is-done   { background: #94A3B8; }
.egp-month-chip {
  font-size: 0.75rem;
  background: #EEF3F9;
  color: #1C3A5E;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}
.egp-month-chip--empty {
  background: #F1EDE6;
  color: #94A3B8;
}
.egp-member-row__star { display: flex; justify-content: center; }

/* ── Action cards ────────────────────────────────────────────────────── */
.egp-actions-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.egp-action-card {
  background: #fff;
  border: 1px solid #E2DDD4;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.egp-action-card--on   { border-color: #BBF7D0; background: #F0FDF4; }
.egp-action-card--idle { border-color: #E2E8F0; background: #F8FAFC; }
.egp-action-card--danger { border-color: #FECACA; background: #FFF5F5; }
.egp-action-card__icon-wrap { display: flex; align-items: center; margin-bottom: 2px; }
.egp-action-card__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1A1A2E;
  margin: 0;
}
.egp-action-card__title--danger { color: #B91C1C; }
.egp-action-card__desc {
  font-size: 0.83rem;
  color: #64748B;
  line-height: 1.5;
  flex: 1;
}
.egp-action-card__btn { align-self: flex-start; }

/* ── Primary button ──────────────────────────────────────────────────── */
.egp-btn.egp-btn--primary {
  background: #1C3A5E !important;
  color: #fff !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  letter-spacing: 0.01em !important;
  padding: 0 20px !important;
  height: 38px !important;
  box-shadow: none !important;
  transition: background 0.15s !important;
}
.egp-btn.egp-btn--primary:hover { background: #16304F !important; }

/* ── Dialogs ─────────────────────────────────────────────────────────── */
.egp-dialog { border-radius: 12px !important; overflow: hidden; }
.egp-dialog__header {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  font-size: 0.95rem;
  font-weight: 700;
  color: #fff;
}
.egp-dialog__header--warn   { background: #B45309; }
.egp-dialog__header--danger { background: #B91C1C; }
.egp-dialog__body { padding: 20px; }
.egp-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #F1EDE6;
}

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .egp-body { flex-direction: column; }
  .egp-sidenav {
    flex: none;
    width: 100%;
    flex-direction: row;
    position: static;
    gap: 2px;
    flex-wrap: wrap;
  }
  .egp-sidenav__foot { display: none; }
  .egp-sidenav__item { flex: 1; justify-content: center; min-width: 0; padding: 8px 6px; }
  .egp-sidenav__label { display: none; }
  .egp-banner__stats { display: none; }
  .egp-form-grid--2 { grid-template-columns: 1fr; }
  .egp-form-grid--3 { grid-template-columns: 1fr; }
  .egp-actions-grid { grid-template-columns: 1fr; }
  .egp-computed-strip { flex-direction: column; }
  .egp-computed-item { border-right: none; border-bottom: 1px solid #E2DDD4; }
  .egp-computed-item:last-child { border-bottom: none; }
  .egp-members-list__head { grid-template-columns: 70px 1fr 90px; }
  .egp-members-list__head span:nth-child(4),
  .egp-members-list__head span:nth-child(5) { display: none; }
  .egp-member-row { grid-template-columns: 70px 1fr 90px; }
  .egp-member-row__month,
  .egp-member-row__star { display: none; }
  .egp-collector-callout { flex-wrap: wrap; }
  .egp-collector-callout__pool { margin-left: 0; }
  .egp-section { padding: 20px 16px 16px; }
}
</style>